import once from 'once';

const BODY_SELECTOR = '.c-field--name-body .c-field__content';
const EXISTING_NAV_SELECTOR = '.handbook-on-page-nav';
const HEADING_SELECTOR = 'h2, h3';
const EXCLUDED_HEADINGS = new Set([
  'all programs',
  'body',
  'medical programs',
  'related policy',
  'snap',
  'tanf',
]);

function isHandbookDetailPage() {
  const segments = window.location.pathname.split('/').filter(Boolean);

  return segments[0] === 'handbooks' && segments.length >= 3;
}

function normalizeText(text) {
  return text.replace(/\s+/g, ' ').trim();
}

function shouldIncludeHeading(heading) {
  const text = normalizeText(heading.textContent || '');
  const tagName = heading.tagName.toLowerCase();

  if (!text) {
    return false;
  }

  if (tagName === 'h2') {
    return true;
  }

  if (EXCLUDED_HEADINGS.has(text.toLowerCase())) {
    return false;
  }

  if (tagName === 'h3') {
    return /^[a-z][—-]\d/i.test(text);
  }

  return true;
}

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 80);
}

function ensureHeadingId(heading, counts) {
  if (heading.id) {
    return heading.id;
  }

  const base =
    slugify(normalizeText(heading.textContent || 'section')) || 'section';
  const count = counts.get(base) || 0;
  counts.set(base, count + 1);
  heading.id = count ? `${base}-${count + 1}` : base;

  return heading.id;
}

function setContentColumnWidth(row, contentColumn) {
  const hasLeftSidebar = Array.from(row.children).some(
    child =>
      child.tagName.toLowerCase() === 'aside' &&
      child !== contentColumn &&
      !child.matches('aside[role="complementary"]')
  );

  contentColumn.classList.remove('l-constrain');

  Array.from(contentColumn.classList)
    .filter(className => className.startsWith('desktop:grid-col-'))
    .forEach(className => contentColumn.classList.remove(className));

  contentColumn.classList.add(
    hasLeftSidebar ? 'desktop:grid-col-7' : 'desktop:grid-col-10'
  );
}

function createSidebar(row, contentColumn) {
  setContentColumnWidth(row, contentColumn);

  const existingSidebar = Array.from(row.children).find(
    child =>
      child.matches('aside[role="complementary"]') &&
      !child.contains(contentColumn)
  );

  if (existingSidebar) {
    existingSidebar.classList.add('handbook-on-page-nav-region');
    return existingSidebar;
  }

  const sidebar = document.createElement('aside');
  sidebar.className =
    'desktop:grid-col-2 u-padding-x-2 handbook-on-page-nav-region';
  sidebar.setAttribute('role', 'complementary');
  sidebar.setAttribute('aria-label', 'On this page');
  sidebar.setAttribute('id', 'sidebar-second');
  row.append(sidebar);

  return sidebar;
}

function buildNav(headings) {
  const nav = document.createElement('nav');
  nav.className = 'handbook-on-page-nav';
  nav.setAttribute('aria-labelledby', 'handbook-on-page-nav-title');

  const title = document.createElement('h2');
  title.id = 'handbook-on-page-nav-title';
  title.className = 'handbook-on-page-nav__title';
  title.textContent = 'On this page';

  const list = document.createElement('ol');
  list.className = 'handbook-on-page-nav__list';

  const counts = new Map();
  headings.forEach(heading => {
    const id = ensureHeadingId(heading, counts);
    const item = document.createElement('li');
    const link = document.createElement('a');
    const level = heading.tagName.toLowerCase() === 'h3' ? 3 : 2;

    item.className = `handbook-on-page-nav__item handbook-on-page-nav__item--level-${level}`;
    link.href = `#${id}`;
    link.textContent = normalizeText(heading.textContent || '');
    item.append(link);
    list.append(item);
  });

  nav.append(title, list);

  return nav;
}

function observeActiveHeading(nav, headings) {
  const links = Array.from(nav.querySelectorAll('a'));

  if (!('IntersectionObserver' in window) || !links.length) {
    return;
  }

  const setActive = id => {
    links.forEach(link => {
      const isActive = link.getAttribute('href') === `#${id}`;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const observer = new IntersectionObserver(
    entries => {
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

      if (visible[0]) {
        setActive(visible[0].target.id);
      }
    },
    {
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0,
    }
  );

  headings.forEach(heading => observer.observe(heading));
}

function attachOnPageNav(bodyField) {
  const headings = Array.from(
    bodyField.querySelectorAll(HEADING_SELECTOR)
  ).filter(shouldIncludeHeading);

  if (!headings.length) {
    return;
  }

  const main = bodyField.closest('main');
  const contentColumn = main ? main.parentElement : null;
  const row = contentColumn ? contentColumn.parentElement : null;

  if (!row || !contentColumn || !row.classList.contains('grid-row')) {
    return;
  }

  const sidebar = createSidebar(row, contentColumn);
  sidebar.querySelector(EXISTING_NAV_SELECTOR)?.remove();

  const nav = buildNav(headings);
  sidebar.prepend(nav);
  observeActiveHeading(nav, headings);
}

export default function handbookOnPageNav(context) {
  if (!isHandbookDetailPage()) {
    return;
  }

  once('handbook-on-page-nav', BODY_SELECTOR, context).forEach(attachOnPageNav);
}
